import { useTranslation } from "react-i18next";
import { type Portfolio } from "../../lib/api";
import { NextStepCard, StatCard } from "./Cards";

const HOST = window.location.hostname === "localhost" ? "professionals.test.wiebe.xyz" : window.location.hostname;

// ─── next-steps guide (portfolio exists but nothing in it yet) ────────────────

export function GettingStartedGuide({
  portfolio,
  stats,
  onNewInquiry,
}: {
  portfolio: Portfolio;
  stats: {
    galleries: number;
    clients: number;
    leads: number;
    bookings: number;
    isPublished: boolean;
  };
  onNewInquiry: () => void;
}) {
  const { t } = useTranslation();
  const portfolioURL = `https://${HOST}/${portfolio.Slug}`;
  const allDone =
    stats.galleries > 0 &&
    stats.clients > 0 &&
    stats.isPublished &&
    portfolio.Bio.trim().length > 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "0.75rem",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <h1 style={{ margin: "0 0 0.25rem" }}>
            Welcome, {portfolio.DisplayName.split(" ")[0]}
          </h1>
          <p style={{ margin: 0, color: "var(--muted)" }}>
            {allDone
              ? "You're all set. Here's your studio at a glance."
              : "A few steps to get the most out of your portfolio."}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button className="btn-primary" onClick={onNewInquiry} style={{ fontSize: "0.875rem", whiteSpace: "nowrap" }}>
            {t("dashboard.new_inquiry")}
          </button>
          <a
            href={portfolioURL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
            style={{ fontSize: "0.875rem", whiteSpace: "nowrap" }}
          >
            {t("dashboard.view_site")}
          </a>
        </div>
      </div>

      {!allDone && (
        <div style={{ display: "grid", gap: "0.75rem", marginBottom: "2rem" }}>
          <NextStepCard
            icon="🌐"
            title={t("dashboard.setup_publish")}
            description="Your site is currently hidden. Toggle Publish in the portfolio editor to go live."
            cta="Open editor"
            href="/app/portfolio"
            done={stats.isPublished}
          />
          <NextStepCard
            icon="📸"
            title={t("dashboard.setup_gallery")}
            description="Upload photos from a shoot, then share a private link with your client."
            cta="Create gallery"
            href="/app/galleries"
            done={stats.galleries > 0}
          />
          <NextStepCard
            icon="✏️"
            title={t("dashboard.setup_bio")}
            description="Visitors who find you via Google or Instagram will want to know your story."
            cta="Edit portfolio"
            href="/app/portfolio"
            done={portfolio.Bio.trim().length > 0}
          />
          <NextStepCard
            icon="👤"
            title={t("dashboard.setup_client")}
            description="Track who you're shooting for. Link bookings and galleries to their profile."
            cta="Add client"
            href="/app/clients"
            done={stats.clients > 0}
          />
        </div>
      )}

      <div
        style={{
          display: "grid",
          gap: "1rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        }}
      >
        <StatCard
          title={t("dashboard.upcoming_bookings")}
          value={stats.bookings}
          sub={stats.bookings === 0 ? "none yet" : undefined}
          href="/app/bookings"
        />
        <StatCard
          title={t("dashboard.galleries")}
          value={stats.galleries}
          sub={stats.galleries === 0 ? "none yet" : undefined}
          href="/app/galleries"
        />
        <StatCard
          title={t("dashboard.clients")}
          value={stats.clients}
          sub={stats.clients === 0 ? "none yet" : undefined}
          href="/app/clients"
        />
        <StatCard
          title={t("dashboard.leads")}
          value={stats.leads}
          sub={stats.leads === 0 ? t("dashboard.stat_from_form") : `${stats.leads} enquir${stats.leads === 1 ? "y" : "ies"}`}
          href="/app/leads"
        />
      </div>
    </div>
  );
}

